/**
 * Aplicar um ajuste, com a ordem que garante a trilha (spec 2026-09-24-robo-
 * painel-de-ajustes, 7): o SERVIDOR primeiro, e a serial só depois do 200, com
 * a versão que ele devolveu. Não existe ajuste pela USB que o servidor não
 * conheça: se o `PUT` falhar, a exceção sobe e nada vai para a porta.
 *
 * O que vai pela serial é o que o firmware de fato aceita (`DirectConsole.h`):
 * o estado desejado INTEIRO, o mesmo bloco do snapshot, porque campo ausente
 * volta ao padrão no robô. Só Tela e reinício descem ao firmware; Operação
 * (intervalo e severidade) vale no servidor e não manda nada pela porta.
 */
import type {
  AjustesDesejados, CanalDoAjuste, MudancaDeAjustes, RespostaDeAjuste,
} from '@/api/robo'
import { perguntar } from '@/features/robo-usb/fluxo'
import {
  LINHA_DIAG, LINHA_REINICIAR, interpretarRespostaAjuste, lerDiag, linhaAjuste,
  type BlocoDeAjuste, type Diag,
} from '@/features/robo-usb/protocolo'
import type { Linhas } from '@/features/robo-usb/serial'

/** A resposta serial tem 5 s (spec 7); depois disso, "salvo, falta aplicar". */
export const PRAZO_DO_AJUSTE_MS = 5_000

export type ApiDeAjustes = {
  alterar(mudancas: MudancaDeAjustes, canal: CanalDoAjuste): Promise<RespostaDeAjuste>
  restaurar(canal: CanalDoAjuste): Promise<RespostaDeAjuste>
  reiniciar(canal: CanalDoAjuste): Promise<RespostaDeAjuste>
}

export type PortaDeAjuste = Pick<Linhas, 'escrever' | 'proxima'>

export type ResultadoDoAjuste =
  /** O robô respondeu `AJUSTE_OK` com a versão enviada. */
  | { tipo: 'aplicado'; versao: number; recusado: string[]; resposta: RespostaDeAjuste }
  /** Nada a mandar ao firmware (Operação), ou o canal é a rede: vale pelo snapshot. */
  | { tipo: 'salvo'; versao: number; resposta: RespostaDeAjuste }
  /** Salvo no servidor, e o robô não confirmou em 5 s (ou confirmou outra versão). */
  | { tipo: 'salvo_sem_aplicar'; versao: number; resposta: RespostaDeAjuste }
  /** O firmware não tem o `AJUSTE` (`CONFIG_INVALIDA`). */
  | { tipo: 'firmware_sem_ajustes'; versao: number; resposta: RespostaDeAjuste }
  /** O firmware recusou a linha (`AJUSTE_INVALIDO`). */
  | { tipo: 'recusado_pela_placa'; versao: number; resposta: RespostaDeAjuste }

const DO_FIRMWARE = ['brilho', 'rotacao', 'rotacao_info'] as const

export function tocaOFirmware(mudancas: MudancaDeAjustes): boolean {
  return DO_FIRMWARE.some((c) => c in mudancas)
}

/** O bloco `ajustes` que o snapshot mandaria (`robo_routes.py`): sem as chaves nulas. */
export function blocoDoFirmware(d: AjustesDesejados): BlocoDeAjuste {
  return {
    versao: d.versao,
    ...(d.brilho !== null ? { brilho: d.brilho } : {}),
    ...(d.rotacao !== null ? { rotacao: d.rotacao } : {}),
    ...(d.rotacao_info !== null ? { rotacao_info: d.rotacao_info } : {}),
    reinicio: d.reinicio,
  }
}

async function mandarPelaSerial(
  porta: PortaDeAjuste, bloco: BlocoDeAjuste, resposta: RespostaDeAjuste, agora: () => number,
): Promise<ResultadoDoAjuste> {
  const versao = resposta.versao
  let r
  try {
    r = await perguntar(porta, linhaAjuste(bloco), PRAZO_DO_AJUSTE_MS, interpretarRespostaAjuste, agora)
  } catch {
    // Cabo solto no meio: o desejado já está no servidor.
    return { tipo: 'salvo_sem_aplicar', versao, resposta }
  }
  if (r === undefined) return { tipo: 'salvo_sem_aplicar', versao, resposta }
  if (r.tipo === 'sem_comando') return { tipo: 'firmware_sem_ajustes', versao, resposta }
  if (r.tipo === 'invalido') return { tipo: 'recusado_pela_placa', versao, resposta }
  // O firmware responde a versão que TEM: só é a enviada se ele aplicou.
  if (r.versao !== versao) return { tipo: 'salvo_sem_aplicar', versao, resposta }
  return { tipo: 'aplicado', versao, recusado: r.recusado, resposta }
}

export async function aplicarPelaUsb(
  { api, porta, agora = Date.now }: { api: ApiDeAjustes; porta: PortaDeAjuste; agora?: () => number },
  mudancas: MudancaDeAjustes,
): Promise<ResultadoDoAjuste> {
  const resposta = await api.alterar(mudancas, 'usb')
  if (!tocaOFirmware(mudancas)) return { tipo: 'salvo', versao: resposta.versao, resposta }
  return mandarPelaSerial(porta, blocoDoFirmware(resposta.desejado), resposta, agora)
}

export async function aplicarPelaRede(
  api: ApiDeAjustes, mudancas: MudancaDeAjustes,
): Promise<ResultadoDoAjuste> {
  const resposta = await api.alterar(mudancas, 'rede')
  return { tipo: 'salvo', versao: resposta.versao, resposta }
}

/** "Restaurar padrões" pela USB: `AJUSTE {"padrao":true,"versao":N}` (spec 5.2). */
export async function restaurarPelaUsb(
  { api, porta, agora = Date.now }: { api: ApiDeAjustes; porta: PortaDeAjuste; agora?: () => number },
): Promise<ResultadoDoAjuste> {
  const resposta = await api.restaurar('usb')
  return mandarPelaSerial(porta, { versao: resposta.versao, padrao: true }, resposta, agora)
}

export async function restaurarPelaRede(api: ApiDeAjustes): Promise<ResultadoDoAjuste> {
  const resposta = await api.restaurar('rede')
  return { tipo: 'salvo', versao: resposta.versao, resposta }
}

export type ResultadoDoReinicio = 'reiniciando' | 'pedido' | 'sem_resposta' | 'firmware_sem_ajustes'

/**
 * Pela USB o servidor só registra (o contador não anda, senão o próximo
 * snapshot reiniciaria de novo), e quem reinicia é o `REINICIAR`.
 */
export async function reiniciarPelaUsb(
  { api, porta, agora = Date.now }: { api: ApiDeAjustes; porta: PortaDeAjuste; agora?: () => number },
): Promise<ResultadoDoReinicio> {
  await api.reiniciar('usb')
  try {
    const r = await perguntar<ResultadoDoReinicio>(porta, LINHA_REINICIAR, PRAZO_DO_AJUSTE_MS, (l) => {
      if (l === 'REINICIANDO') return 'reiniciando'
      if (l === 'CONFIG_INVALIDA') return 'firmware_sem_ajustes'
      return undefined
    }, agora)
    return r ?? 'sem_resposta'
  } catch {
    return 'sem_resposta'
  }
}

/** Pela rede, o robô reinicia ao ver o contador maior na próxima consulta. */
export async function reiniciarPelaRede(api: ApiDeAjustes): Promise<ResultadoDoReinicio> {
  await api.reiniciar('rede')
  return 'pedido'
}

/**
 * `DIAG` pela serial. `null` = firmware sem o comando; `undefined` = não
 * respondeu. Só leitura: não passa pelo servidor.
 */
export async function lerDiagPelaUsb(
  porta: PortaDeAjuste, agora: () => number = Date.now, prazoMs = PRAZO_DO_AJUSTE_MS,
): Promise<Diag | null | undefined> {
  return perguntar(porta, LINHA_DIAG, prazoMs, (l) => {
    if (l.startsWith('DIAG ')) return lerDiag(l)
    if (l === 'CONFIG_INVALIDA') return null
    return undefined
  }, agora)
}
